import { commissionPercent } from "../../../../supabase/functions/_shared/clinicianSettlementPolicy.mjs";
import React from "react";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import PreferencesPageShell from "@/features/settings/preferences/PreferencesPageShell.jsx";
import { getBillingHistory } from "@/services/billingLedgerService.js";
import { loadDoctorBillingReadiness } from "@/services/doctorBillingProfileService.js";

function downloadBlob(filename, payload, type = "application/json") {
  const blob =
    payload instanceof Blob ? payload : new Blob([payload], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  downloadBlob(filename, csv, "text/csv;charset=utf-8;");
}

function formatDate(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatReady(value) {
  return value ? "Ready" : "Not ready";
}

async function downloadSimplePdf(title, sections = []) {
  const mod = await import("jspdf");
  const JsPdf = mod?.jsPDF || mod?.default;
  const doc = new JsPdf({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 48;
  const topMargin = 56;
  const bottomMargin = 56;
  const lineHeight = 18;
  const contentWidth = pageWidth - marginX * 2;
  let y = topMargin;

  const ensureSpace = (needed = lineHeight) => {
    if (y + needed <= pageHeight - bottomMargin) return;
    doc.addPage();
    y = topMargin;
  };

  const writeWrapped = (text, fontSize = 12, color = [35, 49, 70]) => {
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(String(text || ""), contentWidth);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, marginX, y);
      y += lineHeight;
    });
  };

  doc.setFont("helvetica", "bold");
  writeWrapped(title, 20, [4, 8, 18]);
  y += 4;
  doc.setFont("helvetica", "normal");
  writeWrapped(`Exported ${new Date().toLocaleString()}`, 11, [91, 107, 134]);
  y += 10;

  sections.forEach((section) => {
    ensureSpace(28);
    doc.setFont("helvetica", "bold");
    writeWrapped(section?.title || "", 14, [4, 8, 18]);
    y += 2;

    doc.setFont("helvetica", "normal");
    (section?.rows || []).forEach((row) => {
      writeWrapped(`${row.label}: ${row.value}`, 12, [35, 49, 70]);
    });

    y += 10;
  });

  doc.save(`${String(title || "export").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
}

export default function DownloadsPage({ role = "patient" }) {
  const { t } = useLanguage();
  const auth = useAuth() || {};
  const userId = auth?.user?.id || null;
  const [downloading, setDownloading] = React.useState("");

  async function exportBillingSnapshot() {
    setDownloading("billing");
    try {
      const result = await getBillingHistory(role, userId);
      const rows = Array.isArray(result?.data) ? result.data : [];

      downloadCsv(
        `${role}-billing-history.csv`,
        [
          ["Created", "Status", "Amount", "Currency", "Paid At", "Invoice", "Reference"],
          ...rows.map((row) => [
            formatDate(row?.created_at),
            row?.status || "",
            row?.amount || "",
            row?.currency || "",
            formatDate(row?.paid_at),
            row?.invoice_number || "",
            row?.reference || "",
          ]),
        ]
      );
    } finally {
      setDownloading("");
    }
  }

  async function exportBillingLaneSnapshot() {
    setDownloading("billing-lane");

    try {
      if (role === "doctor") {
        const [billingResult, readiness] = await Promise.all([
          getBillingHistory(role, userId),
          loadDoctorBillingReadiness(userId),
        ]);
        const rows = Array.isArray(billingResult?.data) ? billingResult.data : [];
        await downloadSimplePdf("Doctor Billing Readiness", [
          {
            title: "Account",
            rows: [
              { label: "Email", value: auth?.user?.email || "Not available" },
              { label: "Billing records", value: rows.length },
            ],
          },
          {
            title: "Billing Setup",
            rows: [
              { label: "Active services", value: readiness.servicesCount },
              { label: "Consulting locations", value: readiness.consultanciesCount },
              { label: "Payout account verified", value: formatReady(readiness.payoutReady) },
              { label: "Doctor signature", value: formatReady(readiness.signatureReady) },
              { label: "Practice logo", value: formatReady(readiness.practiceLogoReady) },
              { label: "Agreement archive", value: formatReady(readiness.agreementReady) },
              { label: "Agreement version", value: readiness.agreementVersion || "Not available" },
              { label: "Agreement signed", value: formatDate(readiness.agreementSignedAt) },
            ],
          },
          {
            title: "Payments",
            rows: [
              {
                label: "Enabled payout methods",
                value: readiness.paymentMethodLabels?.length
                  ? readiness.paymentMethodLabels.join(", ")
                  : "Not configured",
              },
              { label: "AfriMed commission", value: `${commissionPercent}%` },
            ],
          },
        ]);
        return;
      }

      const billingResult = await getBillingHistory(role, userId);
      const rows = Array.isArray(billingResult?.data) ? billingResult.data : [];
      const paidRows = rows.filter(
        (row) => String(row?.status || "").toLowerCase() === "paid"
      );
      const pendingRows = rows.filter(
        (row) => String(row?.status || "").toLowerCase() !== "paid"
      );
      const totalPaid = paidRows.reduce(
        (sum, row) => sum + Number(row?.amount || 0),
        0
      );

      await downloadSimplePdf("Patient Payment Lane Snapshot", [
        {
          title: "Account",
          rows: [
            { label: "Email", value: auth?.user?.email || "Not available" },
            { label: "Billing records", value: rows.length },
            { label: "Paid records", value: paidRows.length },
            { label: "Pending records", value: pendingRows.length },
          ],
        },
        {
          title: "Payments",
          rows: [
            {
              label: "Total paid",
              value: totalPaid
                ? new Intl.NumberFormat("en-CH", {
                    style: "currency",
                    currency: rows?.[0]?.currency || "CHF",
                  }).format(totalPaid)
                : "CHF 0.00",
            },
            {
              label: "Latest invoice",
              value:
                rows?.[0]?.invoice_number ||
                rows?.[0]?.reference ||
                "Not available",
            },
            {
              label: "Latest paid date",
              value: formatDate(paidRows?.[0]?.paid_at),
            },
          ],
        },
        {
          title: "AfriMed™ lane",
          rows: [
            {
              label: "Exported",
              value: new Date().toLocaleString(),
            },
            {
              label: "Purpose",
              value:
                "Receipts, sponsor payments, and quick account-side reconciliation.",
            },
          ],
        },
      ]);
    } finally {
      setDownloading("");
    }
  }

  async function exportProfileSnapshot() {
    setDownloading("profile");
    try {
      if (role === "doctor") {
        const profile = auth?.profile || {};
        await downloadSimplePdf("Doctor Settings Profile", [
          {
            title: "Identity",
            rows: [
              { label: "Email", value: auth?.user?.email || "Not available" },
              {
                label: "Full name",
                value:
                  profile?.full_name ||
                  profile?.display_name ||
                  auth?.user?.user_metadata?.full_name ||
                  "Not available",
              },
              {
                label: "Practice",
                value:
                  profile?.practice_name ||
                  profile?.clinic_name ||
                  "Not available",
              },
              { label: "Country", value: profile?.country || "Not available" },
              { label: "Phone", value: profile?.phone || "Not available" },
            ],
          },
          {
            title: "Profile",
            rows: [
              { label: "Specialty", value: profile?.specialty || "Not available" },
              { label: "Languages", value: profile?.languages || "Not available" },
              { label: "Registration number", value: profile?.registration_number || "Not available" },
            ],
          },
        ]);
        return;
      }

      const profile = auth?.profile || {};
      await downloadSimplePdf("Patient Settings Profile", [
        {
          title: "Identity",
          rows: [
            { label: "Email", value: auth?.user?.email || "Not available" },
            {
              label: "Full name",
              value:
                profile?.full_name ||
                profile?.display_name ||
                auth?.user?.user_metadata?.full_name ||
                "Not available",
            },
            { label: "Country", value: profile?.country || "Not available" },
            { label: "Phone", value: profile?.phone || "Not available" },
          ],
        },
        {
          title: "Settings state",
          rows: [
            {
              label: "Profile updated",
              value: formatDate(profile?.updated_at || profile?.created_at),
            },
            {
              label: "Exported",
              value: new Date().toLocaleString(),
            },
          ],
        },
      ]);
    } finally {
      setDownloading("");
    }
  }

  const downloadItems = [
    {
      key: "billing",
      title: t(
        "settings.preferences.downloads.billingSnapshot",
        "Billing snapshot"
      ),
      subtitle: t(
        "settings.preferences.downloads.billingSnapshotSubtitle",
        role === "doctor"
          ? "Download billing rows as CSV for accounting, reconciliation, or spreadsheet work."
          : "Download billing rows as CSV for receipts, reimbursement, or spreadsheet work."
      ),
      onClick: exportBillingSnapshot,
    },
    {
      key: "billing-lane",
      title: t(
        "settings.preferences.downloads.subscriptionSnapshot",
        role === "doctor"
          ? "Billing readiness snapshot"
          : "Payment lane snapshot"
      ),
      subtitle: t(
        "settings.preferences.downloads.subscriptionSnapshotSubtitle",
        role === "doctor"
          ? "Download the latest AfriMed™ billing readiness and payout setup overview as a PDF."
          : "Download a PDF summary of your payment lane, receipts, and sponsor-payment activity."
      ),
      onClick: exportBillingLaneSnapshot,
    },
    {
      key: "profile",
      title: t(
        "settings.preferences.downloads.profileSnapshot",
        "Settings profile"
      ),
      subtitle: t(
        "settings.preferences.downloads.profileSnapshotSubtitle",
        role === "doctor"
          ? "Download a PDF summary of your current doctor settings and identity details."
          : "Download a PDF summary of your current patient identity and settings state."
      ),
      onClick: exportProfileSnapshot,
    },
  ];

  return (
    <PreferencesPageShell
      role={role}
      title={t("settings.preferences.downloads.title", "Downloads")}
      subtitle={t(
        "settings.preferences.downloads.subtitleLong",
        "Keep quick AfriMed™ exports nearby while fuller archive tooling continues to grow."
      )}
    >
      <div className="space-y-3">
        {downloadItems.map((item) => (
          <Card as="section" key={item.key} className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="text-title-small text-on-surface">
                  {item.title}
                </div>
                <div className="mt-1 text-body-medium text-on-surface-var">
                  {item.subtitle}
                </div>
              </div>

              <Button
                type="button"
                onClick={item.onClick}
                variant="default"
                size="default"
                className="w-full sm:w-auto"
              >
                {downloading === item.key
                  ? t("common.loading", "Loading...")
                  : t("common.download", "Download")}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card as="section" className="p-4">
        <div className="text-title-small text-on-surface">
          {t(
            "settings.preferences.downloads.comingNext",
            "Coming next"
          )}
        </div>
        <div className="mt-2 text-body-medium text-on-surface-var">
          {t(
            "settings.preferences.downloads.comingNextSubtitle",
            "Full archive bundles, invoice PDFs, and richer AfriMed™ record exports will be added here without changing this navigation."
          )}
        </div>
      </Card>
    </PreferencesPageShell>
  );
}
